import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Globe,
  Network,
  Mail,
  Fingerprint,
  User,
  Terminal,
  BookOpen,
  Shield,
  GraduationCap,
  Wrench,
  History as HistoryIcon,
  LogIn,
  UserCircle,
  Sparkles,
  Lock,
  Hash,
  Code2,
  Image as ImageIcon,
  Database,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { CommandHint } from "@/components/ui/operator";

interface CmdItem {
  id: string;
  label: string;
  hint?: string;
  group: "Navegación" | "Herramientas" | "OSINT" | "Cuenta" | "Atajos";
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
  action?: () => void;
  keywords?: string;
  authRequired?: boolean;
}

function buildItems(navigate: (to: string) => void, isAuthed: boolean): CmdItem[] {
  return [
    // Navigation
    { id: "home", label: "Ir a inicio", group: "Navegación", icon: Sparkles, to: "/", keywords: "home landing principal" },
    { id: "tools", label: "Herramientas", group: "Navegación", icon: Wrench, to: "/arsenal", keywords: "tools utilities" },
    { id: "knowledge", label: "Conocimiento", group: "Navegación", icon: BookOpen, to: "/knowledge", keywords: "academy aprender" },
    { id: "research", label: "Research / Writeups", group: "Navegación", icon: Database, to: "/knowledge/research", keywords: "investigacion vulnerability" },
    { id: "learning", label: "Rutas de aprendizaje", group: "Navegación", icon: GraduationCap, to: "/knowledge/learning" },
    { id: "awareness", label: "Conciencia digital", group: "Navegación", icon: Shield, to: "/conciencia", keywords: "concienciacion phishing" },
    { id: "history", label: "Historial de operaciones", group: "Navegación", icon: HistoryIcon, to: "/history", authRequired: true },

    // OSINT
    { id: "osint-domain", label: "Domain Recon", hint: "WHOIS · DNS · SSL", group: "OSINT", icon: Globe, to: "/arsenal?tool=domain_recon", keywords: "dominio dns whois" },
    { id: "osint-ip", label: "IP Intelligence", hint: "Geo · ASN · Reputación", group: "OSINT", icon: Network, to: "/arsenal?tool=ip_intelligence", keywords: "ip geolocalizacion asn" },
    { id: "osint-email", label: "Email Recon", hint: "Deep OSINT", group: "OSINT", icon: Fingerprint, to: "/arsenal?tool=email_recon" },
    { id: "osint-hunter", label: "Email Hunter", hint: "Emails corporativos", group: "OSINT", icon: Mail, to: "/arsenal?tool=email_hunter" },
    { id: "osint-username", label: "Username Search", hint: "150+ plataformas", group: "OSINT", icon: User, to: "/arsenal?tool=username_search" },

    // Tools
    { id: "tool-encoder", label: "Encoder / Decoder", group: "Herramientas", icon: Code2, to: "/arsenal?tool=encoder" },
    { id: "tool-hash", label: "Hash Tools", group: "Herramientas", icon: Hash, to: "/arsenal?tool=hash" },
    { id: "tool-image", label: "Image Analyzer", group: "Herramientas", icon: ImageIcon, to: "/arsenal?tool=image" },
    { id: "tool-console", label: "Terminal avanzada", group: "Herramientas", icon: Terminal, to: "/arsenal?tool=console" },

    // Cuenta
    isAuthed
      ? { id: "account", label: "Mi cuenta", group: "Cuenta", icon: UserCircle, to: "/account" }
      : { id: "auth", label: "Iniciar sesión / Registrarse", group: "Cuenta", icon: LogIn, to: "/auth" },
    { id: "legal", label: "Legal · Privacidad · Cookies", group: "Cuenta", icon: Lock, to: "/legal" },

    // Shortcuts
    { id: "scroll-top", label: "Volver arriba", group: "Atajos", icon: Sparkles, action: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
  ].filter(Boolean) as CmdItem[];
}

function fuzzy(item: CmdItem, q: string): number {
  if (!q) return 1;
  const hay = `${item.label} ${item.hint ?? ""} ${item.keywords ?? ""} ${item.group}`.toLowerCase();
  const needle = q.toLowerCase().trim();
  if (hay.includes(needle)) return 2;
  // letter-by-letter subsequence
  let i = 0;
  for (const c of needle) {
    const found = hay.indexOf(c, i);
    if (found < 0) return 0;
    i = found + 1;
  }
  return 1;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = React.useState("");
  const [active, setActive] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const items = React.useMemo(() => buildItems(navigate, !!user), [navigate, user]);

  const filtered = React.useMemo(() => {
    return items
      .map((it) => ({ it, score: fuzzy(it, q) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.it);
  }, [items, q]);

  // Group results
  const grouped = React.useMemo(() => {
    const map: Record<string, CmdItem[]> = {};
    for (const it of filtered) (map[it.group] ||= []).push(it);
    return map;
  }, [filtered]);

  const flatOrder = React.useMemo(() => Object.values(grouped).flat(), [grouped]);

  React.useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  React.useEffect(() => setActive(0), [q]);

  const run = (it: CmdItem) => {
    if (it.authRequired && !user) {
      onOpenChange(false);
      navigate("/auth");
      return;
    }
    if (it.action) it.action();
    else if (it.to) navigate(it.to);
    onOpenChange(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flatOrder.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const it = flatOrder[active]; if (it) run(it); }
    else if (e.key === "Escape") { e.preventDefault(); onOpenChange(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[12vh] px-4 animate-fade-in-up"
      role="dialog"
      aria-modal
      onClick={() => onOpenChange(false)}
    >
      <div className="absolute inset-0 bg-background/80 backdrop-blur-md" />
      <div
        className="relative w-full max-w-2xl module-surface clip-corner-sm shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title bar */}
        <div className="module-titlebar">
          <div className="flex items-center gap-2">
            <span className="dot dot-on animate-pulse-signal" />
            <span>CMD · OPERATOR PALETTE</span>
          </div>
          <div className="flex items-center gap-2 text-foreground/60">
            <CommandHint keys={["↑", "↓"]} />
            <span>navegar</span>
            <CommandHint keys={["↵"]} />
            <span>ejecutar</span>
            <CommandHint keys={["esc"]} />
          </div>
        </div>

        {/* Search input */}
        <div className="flex items-center gap-3 px-4 h-14 border-b border-border bg-surface-1">
          <Search className="w-4 h-4 text-primary shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Buscar acción, herramienta, módulo…"
            className="flex-1 bg-transparent border-0 outline-none text-foreground font-mono text-sm placeholder:text-muted-foreground/60 placeholder:font-sans"
          />
          <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">
            {flatOrder.length} resultados
          </span>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto">
          {flatOrder.length === 0 && (
            <div className="p-10 text-center font-mono text-xs text-muted-foreground uppercase tracking-wider">
              <span className="text-primary">//</span> sin coincidencias
            </div>
          )}
          {Object.entries(grouped).map(([group, list]) => (
            <div key={group}>
              <div className="sticky top-0 bg-surface-2/95 backdrop-blur-sm px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-b border-border">
                <span className="text-primary">//</span> {group}
              </div>
              {list.map((it) => {
                const idx = flatOrder.indexOf(it);
                const isActive = idx === active;
                return (
                  <button
                    key={it.id}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => run(it)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors group",
                      isActive ? "bg-primary/10 rail-active" : "hover:bg-surface-2",
                    )}
                  >
                    <div className={cn(
                      "w-7 h-7 flex items-center justify-center border",
                      isActive ? "border-primary bg-primary/10" : "border-border bg-surface-2"
                    )}>
                      <it.icon className={cn("w-3.5 h-3.5", isActive ? "text-primary" : "text-foreground/70")} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-foreground truncate">{it.label}</div>
                      {it.hint && <div className="text-[11px] text-muted-foreground font-mono truncate">{it.hint}</div>}
                    </div>
                    {it.authRequired && !user && (
                      <span className="font-mono text-[10px] uppercase tracking-wider text-signal-amber">auth</span>
                    )}
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[10px] text-primary">↵</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* Hook to wire ⌘K / Ctrl+K globally */
export function useCommandPalette() {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return { open, setOpen };
}